import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Fab,
  Grid,
  Divider,
  ButtonBase,
} from "@mui/material";
import {
  Search as SearchIcon,
  EventNote as EventNoteIcon,
  HourglassEmpty as PendingIcon,
  CheckCircleOutline as ApprovedIcon,
  CancelOutlined as RejectedIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  CalendarTodayOutlined as CalendarIcon,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";

const NAVY = BRAND.navy;
const BG = "#F8FAFC";

const StatCard = ({ icon, label, value, iconBg, iconColor, onClick, isActive }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: "pointer",
      bgcolor: "#fff",
      borderRadius: "12px",
      p: 1.25,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 1.5,
      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
      border: isActive
        ? `1px solid var(--brand-orange, #E65A00)`
        : `1px solid ${BRAND.border}`,
      height: "100%",
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        sx={{
          fontSize: "1.1rem",
          fontWeight: 700,
          color: NAVY,
          lineHeight: 1.1,
          mb: 0.25,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.7rem",
          color: BRAND.muted,
          fontWeight: 500,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
);

const formatLeaveType = (type) =>
  type ? type.charAt(0).toUpperCase() + type.slice(1) : "Leave";

const CardActionButton = ({ icon, label, color, onClick, disabled }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      minHeight: 44,
      py: 1,
      borderRadius: "10px",
      color: disabled ? BRAND.muted : color,
      opacity: disabled ? 0.5 : 1,
      transition: "background-color 0.15s",
      "&:active:not(:disabled)": { bgcolor: `${color}14` },
    }}
  >
    {icon}
    <Typography
      sx={{
        fontSize: "0.7rem",
        fontWeight: 600,
        color: "inherit",
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
  </ButtonBase>
);

const LeaveCard = ({
  leave,
  isEmployee,
  canApprove,
  onView,
  onStatusChange,
}) => {
  const status = leave.approval_status || "pending";

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "16px",
        mb: 2,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 2px 12px rgba(26, 43, 74, 0.05)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {!isEmployee && (
              <Typography
                sx={{
                  fontWeight: 700,
                  color: NAVY,
                  fontSize: "0.95rem",
                  mb: 0.35,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {leave.employee || "Unknown employee"}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "var(--brand-orange, #E65A00)",
              }}
            >
              {formatLeaveType(leave.leave_type)}
            </Typography>
          </Box>
          <span className={`leave-status-badge ${status}`}>{status}</span>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            color: BRAND.muted,
            mb: 0.5,
          }}
        >
          <CalendarIcon sx={{ fontSize: "0.95rem", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.8125rem" }}>
            {leave.leave_start_date} – {leave.leave_end_date}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted }}>
          {leave.total_leave_days}{" "}
          {Number(leave.total_leave_days) === 1 ? "day" : "days"}
        </Typography>
      </Box>

      <Divider />
      <Box sx={{ display: "flex", px: 0.5, py: 0.5 }}>
        <CardActionButton
          icon={<VisibilityIcon sx={{ fontSize: "1.25rem" }} />}
          label="View"
          color="#2196f3"
          onClick={() => onView(leave)}
        />
        {canApprove && status === "pending" && (
          <>
            <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
            <CardActionButton
              icon={<ApprovedIcon sx={{ fontSize: "1.25rem" }} />}
              label="Approve"
              color="#16a34a"
              onClick={() => onStatusChange(leave.id, "approved")}
            />
            <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
            <CardActionButton
              icon={<RejectedIcon sx={{ fontSize: "1.25rem" }} />}
              label="Reject"
              color="#d32f2f"
              onClick={() => onStatusChange(leave.id, "rejected")}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

const LeaveMobileView = ({
  leaves,
  searchTerm,
  setSearchTerm,
  handleView,
  handleStatusChange,
  openModalForCreate,
  canCreate,
  canApprove,
  isEmployee,
}) => {
  const [statusFilter, setStatusFilter] = useState("all");

  const stats = useMemo(
    () => ({
      all: leaves.length,
      pending: leaves.filter((l) => (l.approval_status || "pending") === "pending")
        .length,
      approved: leaves.filter((l) => l.approval_status === "approved").length,
      rejected: leaves.filter((l) => l.approval_status === "rejected").length,
    }),
    [leaves]
  );

  const filteredList = useMemo(() => {
    if (statusFilter === "all") return leaves;
    return leaves.filter(
      (l) => (l.approval_status || "pending") === statusFilter
    );
  }, [leaves, statusFilter]);

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Typography
        sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY, mb: 2 }}
      >
        {isEmployee ? "My Leaves" : "Leave Requests"}
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <StatCard
            icon={<EventNoteIcon fontSize="small" />}
            label="Total"
            value={stats.all}
            iconBg="rgba(33,150,243,0.15)"
            iconColor="#2196f3"
            onClick={() => setStatusFilter("all")}
            isActive={statusFilter === "all"}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard
            icon={<PendingIcon fontSize="small" />}
            label="Pending"
            value={stats.pending}
            iconBg="rgba(255,152,0,0.15)"
            iconColor="#ff9800"
            onClick={() => setStatusFilter("pending")}
            isActive={statusFilter === "pending"}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard
            icon={<ApprovedIcon fontSize="small" />}
            label="Approved"
            value={stats.approved}
            iconBg="rgba(76,175,80,0.15)"
            iconColor="#4caf50"
            onClick={() => setStatusFilter("approved")}
            isActive={statusFilter === "approved"}
          />
        </Grid>
        <Grid item xs={6}>
          <StatCard
            icon={<RejectedIcon fontSize="small" />}
            label="Rejected"
            value={stats.rejected}
            iconBg="rgba(211,47,47,0.12)"
            iconColor="#d32f2f"
            onClick={() => setStatusFilter("rejected")}
            isActive={statusFilter === "rejected"}
          />
        </Grid>
      </Grid>

      <TextField
        fullWidth
        placeholder="Search by employee, type or status..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            bgcolor: "#fff",
            borderRadius: "12px",
            "& fieldset": { borderColor: BRAND.border },
            "&.Mui-focused fieldset": {
              borderColor: "var(--brand-orange, #E65A00)",
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          ),
        }}
      />

      {filteredList.length > 0 ? (
        filteredList.map((leave) => (
          <LeaveCard
            key={leave.id}
            leave={leave}
            isEmployee={isEmployee}
            canApprove={canApprove}
            onView={handleView}
            onStatusChange={handleStatusChange}
          />
        ))
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "#fff",
            borderRadius: "16px",
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <EventNoteIcon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
            No leave requests yet
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
            {canCreate
              ? "Tap + to submit a leave request."
              : "No leave records match your filters."}
          </Typography>
        </Box>
      )}

      {canCreate && (
        <Fab
          color="primary"
          aria-label="add leave"
          onClick={openModalForCreate}
          size="medium"
          sx={{
            position: "fixed",
            bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))',
            right: 20,
            bgcolor: "var(--brand-orange, #E65A00)",
            "&:hover": { bgcolor: "var(--brand-orange-hover, #CC4F00)" },
            boxShadow: "0 4px 16px rgba(230, 90, 0, 0.35)",
            zIndex: 1002,
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default LeaveMobileView;
